import { Box, Button, Divider, Group, Paper, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { AdPlatform } from '@marketing-tool/shared';
import { useState } from 'react';

import { CreativeSelector } from '../../../components/CreativeSelector';
import type { AdGroup, Campaign } from '../../../data/game-report/types';
import type { Game } from '../../../data/games';
import type { CreativeMediaItem } from '../../../data/media';
import { plural, preventEnterSubmit } from '../../../lib/utils';

export type CreateAdFormData = {
    platform: AdPlatform;
    adGroupId: string;
    name: string;
    creativeMediaIds: string[];
    title: string;
    description: string;
    landingPageUrl: string;
};

type CreateAdFormProps = {
    platform: AdPlatform;
    adGroup: AdGroup;
    campaign: Campaign;
    game: Game;
    onSubmit: (data: CreateAdFormData) => void;
    onCancel: () => void;
    isSubmitting?: boolean;
    initialData?: Partial<CreateAdFormData>;
};

export const CreateAdForm = ({
    platform,
    adGroup,
    campaign,
    game,
    onSubmit,
    onCancel,
    isSubmitting,
    initialData,
}: CreateAdFormProps) => {
    const form = useForm<CreateAdFormData>({
        initialValues: {
            platform,
            adGroupId: adGroup.id.toString(),
            name: initialData?.name || '',
            creativeMediaIds: initialData?.creativeMediaIds || [],
            title: initialData?.title || '',
            description: initialData?.description || '',
            landingPageUrl: initialData?.landingPageUrl || game.defaultLandingPageUrl || '',
        },
        validate: {
            creativeMediaIds: (value) => {
                if (value.length === 0) return 'Please select at least one creative from your repository';
                return null;
            },
        },
    });

    const [selectedMediaItems, setSelectedMediaItems] = useState<CreativeMediaItem[]>([]);

    const generateTrackingUrl = (baseUrl: string, creativeName: string): string => {
        const trackingPayload = {
            $channel: 'AD',
            $adNetworkName: platform === AdPlatform.Line ? 'LINE' : 'Facebook',
            $adCampaignName: campaign.name,
            $adSetName: adGroup.name,
            $adCreativeName: creativeName,
        };

        const encodedPayload = JSON.stringify(trackingPayload);
        const url = new URL(baseUrl);
        url.searchParams.set('payload', encodedPayload);

        return url.toString();
    };

    const handleSubmit = form.onSubmit((values) => {
        // Generate tracking URL with GCInstant parameters
        const trackingUrl = generateTrackingUrl(values.landingPageUrl, values.name);

        // Submit form data with tracking URL
        onSubmit({
            ...values,
            landingPageUrl: trackingUrl,
        });
    });

    const handleCreativeSelect = (mediaItems: CreativeMediaItem[]) => {
        const firstCreativeOrUndefined = mediaItems[0];
        const creativeName =
            mediaItems.length === 1 && firstCreativeOrUndefined
                ? firstCreativeOrUndefined.name
                : mediaItems.length > 1 && firstCreativeOrUndefined
                  ? `${firstCreativeOrUndefined.name} +${mediaItems.length - 1} more`
                  : '';

        setSelectedMediaItems(mediaItems);
        form.setFieldValue('name', creativeName);
        form.setFieldValue(
            'creativeMediaIds',
            mediaItems.map((item) => item.mediaId),
        );
    };

    return (
        <form onSubmit={handleSubmit} onKeyDown={preventEnterSubmit}>
            <Stack gap="lg">
                <Paper bg="light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))" radius="sm" p="md">
                    <Text fw={600} mb="xs">
                        Ad Group
                    </Text>
                    <Text c="dimmed">{adGroup.name}</Text>
                </Paper>

                <Box>
                    <Text fw={500} mb={4}>
                        Creative Media *
                    </Text>
                    <CreativeSelector
                        gameId={game.id}
                        selectedMediaItems={selectedMediaItems}
                        onSelect={handleCreativeSelect}
                    />
                    {form.errors.creativeMediaIds && (
                        <Text size="xs" c="red" mt={4}>
                            {form.errors.creativeMediaIds}
                        </Text>
                    )}
                </Box>

                <TextInput
                    label="Title"
                    placeholder="Enter ad title"
                    required
                    disabled={isSubmitting}
                    {...form.getInputProps('title')}
                />

                <TextInput
                    label="Description"
                    placeholder="Enter ad description"
                    required
                    disabled={isSubmitting}
                    {...form.getInputProps('description')}
                />

                <TextInput
                    label="Landing Page URL"
                    placeholder="https://example.com"
                    required
                    disabled={isSubmitting}
                    description={
                        !form.errors.landingPageUrl
                            ? 'GCInstant tracking parameters will be automatically added to this URL'
                            : undefined
                    }
                    {...form.getInputProps('landingPageUrl')}
                />

                <Divider />
                <Group justify="flex-end" gap="sm">
                    <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting || form.values.creativeMediaIds.length === 0}>
                        {isSubmitting
                            ? `Creating ${form.values.creativeMediaIds.length} ${plural(form.values.creativeMediaIds.length, 'ad', 'ads')}...`
                            : form.values.creativeMediaIds.length === 0
                              ? 'Create Ad'
                              : `Create ${form.values.creativeMediaIds.length} ${plural(form.values.creativeMediaIds.length, 'ad', 'ads')}`}
                    </Button>
                </Group>
            </Stack>
        </form>
    );
};
